// Setting a term's availability in one action.
//
// The old form was database-shaped: one block per weekday, so "Mon/Wed/Fri 3-6"
// meant filling it in three times and changing it later meant three edits kept
// in step by hand. This asks the question the way a trainer holds it — which
// days, what hours, until when — and writes the rows for you.
//
// The count line under the pills is the point of the whole thing. It updates as
// you tap, and it catches the only two mistakes people actually make: a pattern
// that yields nothing (block shorter than one slot) and a pattern that yields
// four hundred (no end date, noticed in December).

import { useState } from 'react';
import TimeSelect from '../ui/TimeSelect';

const PILLS = [
  [0, 'S', 'Sunday'],
  [1, 'M', 'Monday'],
  [2, 'T', 'Tuesday'],
  [3, 'W', 'Wednesday'],
  [4, 'T', 'Thursday'],
  [5, 'F', 'Friday'],
  [6, 'S', 'Saturday'],
];

const SLOT_LENGTHS = [30, 45, 60, 90];

// Quarter hours, because that's how a barn thinks about turning a horse round.
const CHANGEOVERS = [0, 15, 30, 45];

// The three answers to "until when" that a barn actually gives. "No end date"
// is still offered — some patterns really do run until somebody changes them —
// but it is no longer what you get by leaving a field alone.
const RANGE_PRESETS = [
  { key: '4w', label: '4 weeks', days: 28 },
  { key: '12w', label: '12 weeks', days: 84 },
  { key: 'year', label: 'End of year', endOfYear: true },
  { key: 'open', label: 'No end date', open: true },
];

function addDays(iso, n) {
  const d = new Date(iso + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

function endOfYear(iso) {
  return `${iso.slice(0, 4)}-12-31`;
}

// Re-exported so the two screens using these keep one import path, while the
// functions themselves live where a test can reach them. They were defined
// here, and a bug in a sibling of theirs survived a week because a pure
// function inside a JSX component is one nothing can import.
import { slotsPerDay, splitIntoHours, remainderMinutes } from '../../lib/availabilityPattern';
import { fmtDate } from '../../lib/dates';
import { clock } from '../../lib/shifts';
export { emptyPattern, slotsPerDay } from '../../lib/availabilityPattern';


export default function PatternBuilder({
  value,
  onChange,
  types = [],
  trainers = [],
  isManager = false,
  trainerUserId,
  onTrainerChange,
  onSave,
  onCancel,
  saving = false,
  editing = false,
  totalSlots = null,
}) {
  const p = value;
  const [customRange, setCustomRange] = useState(false);

  const set = (fields) => onChange({ ...p, ...fields });

  const toggleDay = (wd) =>
    set({
      weekdays: p.weekdays.includes(wd)
        ? p.weekdays.filter((d) => d !== wd)
        : [...p.weekdays, wd].sort((a, b) => a - b),
    });

  const applyPreset = (preset) => {
    setCustomRange(false);
    if (preset.open) return set({ effectiveUntil: null });
    if (preset.endOfYear) return set({ effectiveUntil: endOfYear(p.effectiveFrom) });
    set({ effectiveUntil: addDays(p.effectiveFrom, preset.days) });
  };

  const activePreset = () => {
    if (customRange) return null;
    if (!p.effectiveUntil) return 'open';
    for (const r of RANGE_PRESETS) {
      if (r.open) continue;
      const end = r.endOfYear ? endOfYear(p.effectiveFrom) : addDays(p.effectiveFrom, r.days);
      if (end === p.effectiveUntil) return r.key;
    }
    return null;
  };
  const active = activePreset();

  const perDay = slotsPerDay(p);
  const perWeek = perDay * p.weekdays.length * p.capacity;
  const chosenType = types.find((t) => t.id === p.lessonTypeId) ?? null;
  // What a save would actually write. Shown before it happens, because "9 to
  // 12" turning into three separate things is a surprise the first time.
  const hourBlocks = editing ? [] : splitIntoHours(p);
  const leftover = editing ? 0 : remainderMinutes(p);
  const tooShort = perDay === 0;

  return (
    <div className="card p-4 space-y-4">
      <h2 className="font-medium">{editing ? 'Edit Availability' : 'Set Availability'}</h2>

      {isManager && trainers.length > 1 && (
        <div>
          <label className="block text-sm font-medium text-ink-700 mb-1" htmlFor="pat-trainer">
            Trainer
          </label>
          <select
            id="pat-trainer"
            className="input"
            value={trainerUserId}
            onChange={(e) => onTrainerChange(e.target.value)}
          >
            {trainers.map((t) => (
              <option key={t.userId} value={t.userId}>{t.name || t.email}</option>
            ))}
          </select>
        </div>
      )}

      <div>
        <span className="block text-sm font-medium text-ink-700 mb-2">Which days?</span>
        <div className="flex gap-1.5">
          {PILLS.map(([wd, initial, full]) => {
            const on = p.weekdays.includes(wd);
            return (
              <button
                key={wd}
                type="button"
                aria-pressed={on}
                aria-label={full}
                title={full}
                onClick={() => toggleDay(wd)}
                className={
                  'flex-1 h-11 rounded-full border font-medium transition-colors ' +
                  (on
                    ? 'bg-hunter text-white border-hunter'
                    : 'bg-white text-ink-500 border-ink-200 hover:bg-ink-50')
                }
              >
                {initial}
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-8">
        <div>
          <label className="block text-sm font-medium text-ink-700 mb-1">From</label>
          <TimeSelect label="From" value={p.startTime} onChange={(v) => set({ startTime: v })} />
        </div>
        <div>
          <label className="block text-sm font-medium text-ink-700 mb-1">To</label>
          <TimeSelect label="To" value={p.endTime} onChange={(v) => set({ endTime: v })} />
        </div>
      </div>

      {/* WHAT ARE YOU TEACHING — asked first, because it answers the other
          three. It used to be the last field and marked optional, so length,
          changeover and capacity were all typed by hand and then overwritten
          the moment a type was picked. That is why the form read as four
          manual numbers: the defaults existed and arrived too late to save
          anybody the typing. */}
      <div>
        <label className="block text-sm font-medium text-ink-700 mb-1" htmlFor="pat-type">
          What are you teaching?
        </label>
        <select
          id="pat-type"
          className="input"
          value={p.lessonTypeId || ''}
          onChange={(e) => {
            const t = types.find((x) => x.id === e.target.value);
            set({
              lessonTypeId: e.target.value,
              ...(t
                ? {
                    slotMinutes: t.defaultMinutes,
                    capacity: t.defaultCapacity,
                    changeoverMinutes: t.defaultChangeoverMinutes ?? 0,
                  }
                : {}),
            });
          }}
        >
          <option value="">Anything — I&apos;ll set the times myself</option>
          {types.map((t) => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </select>
        <p className="text-xs text-ink-400 mt-1">
          {chosenType
            ? `${chosenType.defaultMinutes} min, ${
                chosenType.defaultCapacity > 1
                  ? `up to ${chosenType.defaultCapacity} riders`
                  : 'one rider'
              }${
                (chosenType.defaultChangeoverMinutes ?? 0) > 0
                  ? `, ${chosenType.defaultChangeoverMinutes} min changeover`
                  : ', back to back'
              } — change any of it below.`
            : 'Pick one and the numbers below fill themselves in.'}
        </p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <div>
          <label className="block text-sm font-medium text-ink-700 mb-1" htmlFor="pat-len">
            Lesson length
          </label>
          <select
            id="pat-len"
            className="input"
            value={p.slotMinutes}
            onChange={(e) => set({ slotMinutes: Number(e.target.value) })}
          >
            {SLOT_LENGTHS.map((m) => (
              <option key={m} value={m}>{m} min</option>
            ))}
            {!SLOT_LENGTHS.includes(p.slotMinutes) && (
              <option value={p.slotMinutes}>{p.slotMinutes} min</option>
            )}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-ink-700 mb-1" htmlFor="pat-gap">
            Changeover
          </label>
          <select
            id="pat-gap"
            className="input"
            value={p.changeoverMinutes ?? 0}
            onChange={(e) => set({ changeoverMinutes: Number(e.target.value) })}
          >
            {CHANGEOVERS.map((m) => (
              <option key={m} value={m}>{m === 0 ? 'None' : `${m} min`}</option>
            ))}
            {!CHANGEOVERS.includes(p.changeoverMinutes ?? 0) && (
              <option value={p.changeoverMinutes}>{p.changeoverMinutes} min</option>
            )}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-ink-700 mb-1" htmlFor="pat-cap">
            Riders per slot
          </label>
          <select
            id="pat-cap"
            className="input"
            value={p.capacity}
            onChange={(e) => set({ capacity: Number(e.target.value) })}
          >
            {[1, 2, 3, 4, 5, 6, 8, 10].map((n) => (
              <option key={n} value={n}>{n === 1 ? '1 (private)' : n}</option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <span className="block text-sm font-medium text-ink-700 mb-2">Repeat until</span>
        <div className="flex flex-wrap gap-1.5 mb-2">
          {RANGE_PRESETS.map((r) => (
            <button
              key={r.key}
              type="button"
              aria-pressed={active === r.key}
              onClick={() => applyPreset(r)}
              className={
                'text-sm px-3 py-1.5 rounded-full border transition-colors ' +
                (active === r.key
                  ? 'bg-ink-800 text-white border-ink-800'
                  : 'bg-white text-ink-600 border-ink-200 hover:bg-ink-50')
              }
            >
              {r.label}
            </button>
          ))}
          <button
            type="button"
            aria-pressed={active === null}
            onClick={() => setCustomRange(true)}
            className={
              'text-sm px-3 py-1.5 rounded-full border transition-colors ' +
              (active === null
                ? 'bg-ink-800 text-white border-ink-800'
                : 'bg-white text-ink-600 border-ink-200 hover:bg-ink-50')
            }
          >
            Pick dates
          </button>
        </div>

        {(customRange || active === null) && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-ink-500 mb-1" htmlFor="pat-from">Starts</label>
              <input
                id="pat-from"
                type="date"
                className="input"
                value={p.effectiveFrom}
                onChange={(e) => set({ effectiveFrom: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-xs text-ink-500 mb-1" htmlFor="pat-until">Ends</label>
              <input
                id="pat-until"
                type="date"
                className="input"
                min={p.effectiveFrom}
                value={p.effectiveUntil || ''}
                onChange={(e) => set({ effectiveUntil: e.target.value || null })}
              />
            </div>
          </div>
        )}
      </div>

      {/* What a save actually writes. "9 to 12" becoming three separate things
          is a surprise the first time, and a surprise about what you just
          created is the kind people undo by deleting everything. */}
      {hourBlocks.length > 1 && (
        <div className="rounded-lg border border-hunter/25 bg-hunter/5 px-3 py-2 text-sm text-hunter">
          Saved as <strong>{hourBlocks.length} hourly blocks</strong> —{' '}
          {hourBlocks.map((b) => clock(b.startTime)).join(', ')} — so you can change one without
          touching the others.
          {leftover > 0 && (
            <span className="block text-xs text-amber-700 mt-0.5">
              The last {leftover} minutes are too short for a {p.slotMinutes}-minute lesson and
              won&apos;t be used.
            </span>
          )}
        </div>
      )}

      {/* The line that makes the commitment concrete. */}
      <div className="rounded-lg bg-ink-50 px-3 py-2 text-sm">
        {p.weekdays.length === 0 ? (
          <span className="text-ink-400">Pick the days you're available.</span>
        ) : tooShort ? (
          <span className="text-rose-700">
            That window is shorter than one {p.slotMinutes}-minute lesson, so it makes no slots.
          </span>
        ) : (
          <span className="text-ink-700">
            <strong>{perWeek}</strong> {perWeek === 1 ? 'slot' : 'slots'} a week
            {totalSlots != null && (
              <>
                {' · '}
                <strong>{totalSlots}</strong> total {totalSlots === 1 ? 'slot' : 'slots'}{' '}
                {p.effectiveUntil ? `through ${prettyDate(p.effectiveUntil)}` : 'in the next 12 weeks'}
              </>
            )}
          </span>
        )}
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          className="btn-primary"
          disabled={saving || p.weekdays.length === 0 || tooShort}
          onClick={onSave}
        >
          {saving ? 'Saving…' : editing ? 'Save These Slots' : 'Add These Slots'}
        </button>
        {onCancel && (
          <button type="button" className="btn-secondary" onClick={onCancel} disabled={saving}>
            Cancel
          </button>
        )}
      </div>
    </div>
  );
}

const prettyDate = (iso) => fmtDate(iso, 'short');
